import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ConfidenceRegion {
  box: { x: number; y: number; width: number; height: number };
  text: string;
  confidence: number;
}

interface ReceiptPreviewProps {
  filePath: string;
  confidenceScore: number;
  confidenceRegions?: ConfidenceRegion[];
  onClose?: () => void;
}

const CONFIDENCE_THRESHOLD = 0.85;

export default function ReceiptPreview({
  filePath,
  confidenceScore,
  confidenceRegions = [],
  onClose,
}: ReceiptPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [urlExpiry, setUrlExpiry] = useState<number>(0);
  const [hoveredRegion, setHoveredRegion] = useState<ConfidenceRegion | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Memoize low-confidence regions calculation
  const lowConfidenceRegions = useMemo(() => 
    (confidenceRegions || []).filter(
      (region) => region.confidence < CONFIDENCE_THRESHOLD
    ),
    [confidenceRegions]
  );

  useEffect(() => {
    const loadImage = async () => {
      // Check if cached URL is still valid (60 min expiry)
      if (imageUrl && Date.now() < urlExpiry) {
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoadError(true);
          toast.error("Authentication required");
          return;
        }

        // Security: Validate file path belongs to user
        if (!filePath.startsWith(`${user.id}/`)) {
          throw new Error("Invalid file path: does not belong to user");
        }

        // Use signed URL with 60-minute cache
        const { data: signedUrlData, error } = await supabase.storage
          .from("receipts")
          .createSignedUrl(filePath, 3600);

        if (error) throw error;
        if (!signedUrlData) throw new Error("Failed to generate signed URL");

        setImageUrl(signedUrlData.signedUrl);
        setUrlExpiry(Date.now() + 3600 * 1000); // Cache for 60 minutes
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Failed to load receipt image:", error);
        }
        setLoadError(true);
        toast.error("Failed to load receipt image");
      }
    };

    loadImage();
  }, [filePath, imageUrl, urlExpiry]);

  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const image = imageRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to match image
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    // Draw the image
    ctx.drawImage(image, 0, 0);

    // Draw low-confidence regions (memoized)
    lowConfidenceRegions.forEach((region) => {
      const { box } = region;
      
      // Draw semi-transparent red box
      ctx.strokeStyle = "rgba(239, 68, 68, 0.8)"; // red-500
      ctx.lineWidth = 3;
      ctx.strokeRect(box.x, box.y, box.width, box.height);
      
      // Fill with very transparent red
      ctx.fillStyle = "rgba(239, 68, 68, 0.15)";
      ctx.fillRect(box.x, box.y, box.width, box.height);
    });
  }, [imageLoaded, lowConfidenceRegions]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    setMousePos({ x: e.clientX, y: e.clientY });

    // Check if mouse is over any low-confidence region (using memoized regions)
    const hoveredRegion = lowConfidenceRegions.find((region) => {
      const { box } = region;
      return (
        x >= box.x &&
        x <= box.x + box.width &&
        y >= box.y &&
        y <= box.y + box.height
      );
    });

    setHoveredRegion(hoveredRegion || null);
  };

  const handleMouseLeave = () => {
    setHoveredRegion(null);
  };

  const lowConfidenceCount = (confidenceRegions || []).filter(
    (r) => r.confidence < CONFIDENCE_THRESHOLD
  ).length;

  return (
    <Card className="p-4 space-y-4">
      {loadError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load receipt. The file may have been deleted or you don't have permission to view it.
          </AlertDescription>
        </Alert>
      ) : (
        <>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-lg">Receipt Preview</h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={confidenceScore >= CONFIDENCE_THRESHOLD ? "default" : "destructive"}>
              Confidence: {(confidenceScore * 100).toFixed(0)}%
            </Badge>
            {lowConfidenceCount > 0 && (
              <span className="text-sm text-muted-foreground">
                {lowConfidenceCount} unclear region{lowConfidenceCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {confidenceScore < CONFIDENCE_THRESHOLD && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            ⚠️ Some parts of this receipt couldn't be read clearly. Please retake or enter totals manually.
          </AlertDescription>
        </Alert>
      )}

      <div className="relative border rounded-lg overflow-hidden bg-gray-50">
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Receipt"
          onLoad={() => setImageLoaded(true)}
          className="hidden"
        />
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="max-w-full h-auto cursor-crosshair"
        />
        
        {hoveredRegion && (
          <div
            className="fixed z-50 bg-destructive text-destructive-foreground px-3 py-2 rounded-md text-sm shadow-lg pointer-events-none"
            style={{
              left: mousePos.x + 15,
              top: mousePos.y + 15,
            }}
          >
            <div className="font-semibold">Unclear region</div>
            <div className="text-xs opacity-90">
              Confidence: {(hoveredRegion.confidence * 100).toFixed(0)}%
            </div>
            <div className="text-xs mt-1">Please retake or confirm manually</div>
          </div>
        )}
      </div>

      {lowConfidenceCount > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          Red boxes indicate areas that couldn't be read clearly. Hover over them for details.
        </p>
      )}
        </>
      )}
    </Card>
  );
}
